import { Component, OnInit, OnDestroy, HostBinding } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subscription, fromEvent } from 'rxjs';

@Component({
  selector: 'app-web-keyboard-listener',
  template: '',
  styles: [':host { display: block; }']
})
export class WebKeyboardListenerComponent implements OnInit, OnDestroy {
  private visualViewportResizeSub: Subscription;
  private focusInSub: Subscription;
  private focusOutSub: Subscription;
  private orientationChangeSub: Subscription;
  private initialViewportHeight: number;
  private keyboardVisible = false;
  private inputFocused = false;

  @HostBinding('style.height.px') componentHeight = 0;

  constructor(private platform: Platform) {}

  ngOnInit() {
    console.log('WebKeyboardListenerComponent initialized');

    // Log platform information
    console.log('Platform information:', {
      mobileweb: this.platform.is('mobileweb'),
      android: this.platform.is('android'),
      cordova: this.platform.is('cordova'),
      capacitor: this.platform.is('capacitor')
    });

    // Only setup listeners for mobile web browsers on Android
    if (this.platform.is('mobileweb') && this.platform.is('android')) {
      if (window.visualViewport) {
        this.initialViewportHeight = window.visualViewport.height;
        console.log('Initial visual viewport height set to:', this.initialViewportHeight);

        // Listen to visual viewport resize events
        this.visualViewportResizeSub = fromEvent(window.visualViewport, 'resize').subscribe(() => this.onViewportResize());

        // Listen to focusin and focusout events
        this.focusInSub = fromEvent(document, 'focusin').subscribe(() => this.onFocusIn());
        this.focusOutSub = fromEvent(document, 'focusout').subscribe(() => this.onFocusOut());

        // Listen to orientation changes
        this.orientationChangeSub = fromEvent(window, 'orientationchange').subscribe(() => {
          console.log('Orientation change detected');
          // Update initial viewport height after orientation change
          setTimeout(() => {
            this.initialViewportHeight = window.visualViewport.height;
            console.log('Orientation changed, updated initial viewport height:', this.initialViewportHeight);
          }, 500); // Delay to allow orientation change to complete
        });
      } else {
        console.log('visualViewport API is not supported on this device/browser.');
      }
    } else {
      console.log('Component not activated: Not running on Android mobile web');
    }
  }

  ngOnDestroy() {
    console.log('WebKeyboardListenerComponent destroyed');
    // Unsubscribe from events to prevent memory leaks
    if (this.visualViewportResizeSub) {
      this.visualViewportResizeSub.unsubscribe();
      console.log('Unsubscribed from visualViewport resize event');
    }
    if (this.focusInSub) {
      this.focusInSub.unsubscribe();
      console.log('Unsubscribed from focusin event');
    }
    if (this.focusOutSub) {
      this.focusOutSub.unsubscribe();
      console.log('Unsubscribed from focusout event');
    }
    if (this.orientationChangeSub) {
      this.orientationChangeSub.unsubscribe();
      console.log('Unsubscribed from orientationchange event');
    }
  }

  private onViewportResize() {
    const currentViewportHeight = window.visualViewport.height;
    console.log('visualViewport resize event detected');
    console.log('Current visual viewport height:', currentViewportHeight);
    console.log('Initial visual viewport height:', this.initialViewportHeight);

    const heightDifference = this.initialViewportHeight - currentViewportHeight + 40;
    console.log('Height difference:', heightDifference);

    // Threshold to determine if keyboard is shown (in pixels)
    const keyboardThreshold = 150;

    console.log('Input focused:', this.inputFocused);
    console.log('Keyboard visible:', this.keyboardVisible);

    if (this.inputFocused && heightDifference > keyboardThreshold && !this.keyboardVisible) {
      // Keyboard is likely shown
      this.keyboardVisible = true;
      this.componentHeight = heightDifference ;
      console.log('Keyboard shown. Component height set to:', this.componentHeight);
    } else if ((!this.inputFocused || heightDifference < keyboardThreshold) && this.keyboardVisible) {
      // Keyboard is likely hidden
      this.keyboardVisible = false;
      this.componentHeight = 0;
      console.log('Keyboard hidden. Component height reset to:', this.componentHeight);
    } else {
      console.log('No change in keyboard visibility');
    }
  }

  private onFocusIn() {
    // An input field is focused
    this.inputFocused = true;
    console.log('Focusin event detected. Input focused:', this.inputFocused);
  }

  private onFocusOut() {
    // An input field lost focus
    this.inputFocused = false;
    console.log('Focusout event detected. Input focused:', this.inputFocused);
  }
}
